import { isKeyOf } from './guards.ts';
import { DIRECTIONS, type Direction } from './protocol.ts';
// Zones à effets : des cases peintes sur une carte (outil « Zone à effets ») qui changent quelque chose pour les
// joueurs qui s'y trouvent, tant qu'ils y sont (vitesse…). Indépendantes des zones de farm et de déclenchement.

/**
 * Un effet d'une zone. `speed` : la vitesse des joueurs est multipliée (0,5 : deux fois plus lents ; 2 : deux fois plus
 * rapides). `slide` : glace — on y glisse tout droit sans pouvoir tourner, jusqu'à en sortir ou buter contre un obstacle.
 * `current` : courant (tapis roulant, courant d'eau, rafale) — il emporte le joueur case après case dans le sens `dir`,
 * sans qu'il puisse aller ailleurs, jusqu'à sortir de la zone ou buter contre un obstacle.
 */
export type ZoneBuff = { type: 'speed'; multiplier: number } | { type: 'slide' } | { type: 'current'; dir: Direction };
export type ZoneBuffType = ZoneBuff['type'];

export const ZONE_BUFFS: Readonly<Record<ZoneBuffType, { label: string; create: () => ZoneBuff }>> = {
  speed: { label: 'Vitesse ×', create: () => ({ type: 'speed', multiplier: 1 }) },
  slide: { label: 'Glissade (glace)', create: () => ({ type: 'slide' }) },
  current: { label: 'Courant (tapis roulant, rafale)', create: () => ({ type: 'current', dir: 'down' }) },
};

export const CURRENT_DIRECTIONS: Readonly<Record<Direction, string>> = { up: 'vers le haut', down: 'vers le bas', left: 'vers la gauche', right: 'vers la droite' };
export const isCurrentDirection = (value: unknown): value is Direction => (DIRECTIONS as unknown[]).includes(value);

export const isZoneBuffType = isKeyOf<ZoneBuffType>(ZONE_BUFFS);

export interface BuffZone {
  id: string;
  name: string;
  color: string;
  /** Effets appliqués sur les cases de la zone, cumulés (deux vitesses se multiplient). */
  buffs: ZoneBuff[];
  /** Cases de la zone ; une case appartient au plus à une zone à effets. */
  cells: [x: number, y: number][];
}

export const BUFF_ZONE_LIMITS = { maxPerMap: 50, maxNameLength: 60, maxBuffs: 8, minSpeed: 0.1, maxSpeed: 5 } as const;
export const BUFF_ZONE_ID_PATTERN = /^[a-z0-9-]{4,40}$/;

export const newBuffZoneId = (): string => `b-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

/** Vitesse des joueurs dans la zone (1 : normale). */
export const zoneSpeed = (zone: Pick<BuffZone, 'buffs'>): number =>
  zone.buffs.reduce((speed, buff) => (buff.type === 'speed' ? speed * buff.multiplier : speed), 1);

/** Case d'une carte (index ligne par ligne) → vitesse des joueurs, pour les zones qui la changent. */
export function speedCells(width: number, zones: readonly Pick<BuffZone, 'buffs' | 'cells'>[]): Map<number, number> {
  const speeds = new Map<number, number>();
  for (const zone of zones) {
    const speed = zoneSpeed(zone);
    if (speed !== 1) for (const [x, y] of zone.cells) speeds.set(y * width + x, speed);
  }
  return speeds;
}

/** Durée d'un pas de glissade (plus rapide qu'en marchant, et la même pour tous). */
export const SLIDE_STEP_MS = 110;

/** Cases (index ligne par ligne) des zones où l'on glisse. */
export function slideCells(width: number, zones: readonly Pick<BuffZone, 'buffs' | 'cells'>[]): Set<number> {
  const cells = new Set<number>();
  for (const zone of zones) if (zone.buffs.some((buff) => buff.type === 'slide')) for (const [x, y] of zone.cells) cells.add(y * width + x);
  return cells;
}

/** Sens du courant de chaque case (index ligne par ligne) des zones qui en ont un (le dernier de la zone l'emporte). */
export function currentCells(width: number, zones: readonly Pick<BuffZone, 'buffs' | 'cells'>[]): Map<number, Direction> {
  const currents = new Map<number, Direction>();
  for (const zone of zones) {
    const current = zone.buffs.findLast((buff) => buff.type === 'current');
    if (current) for (const [x, y] of zone.cells) currents.set(y * width + x, current.dir);
  }
  return currents;
}

/** Effets de la zone, en quelques mots (liste de l'éditeur). */
export function describeBuffs(buffs: readonly ZoneBuff[]): string {
  if (!buffs.length) return 'Aucun effet';
  return buffs.map((buff) => (buff.type === 'speed' ? `vitesse ×${buff.multiplier}` : buff.type === 'slide' ? 'glissade' : `courant ${CURRENT_DIRECTIONS[buff.dir]}`)).join(', ');
}
